import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mail, AlertCircle, X } from 'lucide-react';
import { Button } from './ui';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../store/auth';
import { AuthService } from '../services/auth';
import { auth } from '../config/firebase';

export default function EmailVerificationBanner() {
  const { user, refreshUser } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [firebaseEmailVerified, setFirebaseEmailVerified] = useState(
    () => auth.currentUser?.emailVerified ?? false
  );

  useEffect(() => {
    const syncFirebaseVerification = () => {
      setFirebaseEmailVerified(auth.currentUser?.emailVerified ?? false);
    };

    syncFirebaseVerification();
    const unsubscribe = auth.onIdTokenChanged(() => {
      syncFirebaseVerification();
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (firebaseEmailVerified && user && !user.isEmailVerified) {
      void refreshUser({ forceTokenRefresh: true });
    }
  }, [firebaseEmailVerified, refreshUser, user]);

  const isEmailVerified = Boolean(user?.isEmailVerified) || firebaseEmailVerified;

  // Don't show if dismissed or user is verified (Firebase or backend profile)
  if (isDismissed || isEmailVerified) {
    return null;
  }

  const showEmailVerification =
    location.state?.showEmailVerification || Boolean(user && !isEmailVerified);

  if (!showEmailVerification) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!user?.email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);
    try {
      await AuthService.resendEmailVerification(user.email, {
        returnUrl: `${window.location.origin}${location.pathname}${location.search}`,
        firstName: user.firstName,
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend verification email');
      console.error('Resend verification error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Clear the state from location
    navigate(location.pathname, { replace: true, state: {} });
  };

  return (
    <div
      className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-100"
      role="status"
      aria-live="polite"
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertCircle className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
        <span className="min-w-0 truncate">
          Please verify your email — we sent a link to{' '}
          <strong className="font-semibold">{user?.email}</strong>
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleResendVerification}
          isLoading={isResending}
          className="shrink-0 border-blue-300 bg-white text-blue-800 hover:bg-blue-100 hover:text-blue-900 dark:border-blue-800 dark:bg-blue-900/40 dark:text-blue-100 dark:hover:bg-blue-900/70"
        >
          {isResending ? (
            'Sending…'
          ) : (
            <>
              <Mail className="h-3.5 w-3.5" aria-hidden />
              Resend email
            </>
          )}
        </Button>
        <button
          type="button"
          onClick={() => navigate('/email-verification', {
            state: { email: user?.email }
          })}
          className="hidden text-blue-700 underline underline-offset-2 hover:text-blue-900 sm:inline dark:text-blue-300 dark:hover:text-blue-100"
        >
          Verification page
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss email verification notice"
          className="rounded p-1 text-blue-600 hover:bg-blue-100 hover:text-blue-900 dark:text-blue-400 dark:hover:bg-blue-900/60 dark:hover:text-blue-100"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
